export const APP_PERMISSION_KEYS = [
  "workspace.view",
  "counter.view",
  "counter.edit",
  "dashboard.view",
  "dashboard.export",
  "my_schedule.view",
  "my_schedule.calendar.view",
  "my_schedule.attendance.mark",
  "my_schedule.timeoff.request",
  "my_schedule.availability.submit",
  "my_schedule.notifications.view",
  "my_schedule.analytics.view",
  "my_schedule.calendar.export",
  "schedule_builder.view",
  "schedule.calendar.manage",
  "schedule.requests.review",
  "schedule.availability.review",
  "schedule.export.manage",
  "schedule.templates.manage",
  "schedule.employees.manage",
  "schedule.settings.manage",
  "schedule.attendance.manage",
  "schedule.audit.view",
  "ai_center.view",
  "ai.knowledge.manage",
  "ai.updates.manage",
  "ai.analytics.view",
  "admin.staff.view",
  "admin.staff.account.create",
  "admin.staff.account.delete",
  "admin.staff.account.reset_password",
  "admin.staff.permissions.manage",
  "admin.staff.role.assign_admin",
] as const;

export type AppPermissionKey = (typeof APP_PERMISSION_KEYS)[number];

export type PermissionMatrix = Record<AppPermissionKey, boolean>;

export interface UserPermissionGrant {
  user_id: string;
  permission_key: AppPermissionKey | string;
  granted: boolean;
  updated_at?: string | null;
  updated_by?: string | null;
}

export const EMPTY_PERMISSION_MATRIX: PermissionMatrix = APP_PERMISSION_KEYS.reduce((acc, key) => {
  acc[key] = false;
  return acc;
}, {} as PermissionMatrix);

export type PermissionLocale = "hu" | "en" | "de";

export type LocalizedPermissionText = {
  section: Record<PermissionLocale, string>;
  label: Record<PermissionLocale, string>;
  description: Record<PermissionLocale, string>;
};

export type PermissionDefinition = {
  key: AppPermissionKey;
  text: LocalizedPermissionText;
};

const t = (hu: string, en: string, de: string): Record<PermissionLocale, string> => ({ hu, en, de });

export const PERMISSION_DEFINITIONS: PermissionDefinition[] = [
  {
    key: "workspace.view",
    text: {
      section: t("Munkatér", "Workspace", "Arbeitsbereich"),
      label: t("Munkatér megtekintés", "Workspace view", "Arbeitsbereich anzeigen"),
      description: t("Belépés a staff kezdőoldalra.", "Access the staff workspace home.", "Zugriff auf die Startseite des Arbeitsbereichs."),
    },
  },
  {
    key: "counter.view",
    text: {
      section: t("Számláló", "Counter", "Zähler"),
      label: t("Számláló megtekintés", "Counter view", "Zähler anzeigen"),
      description: t("Látogató számláló megnyitása.", "Open the visitor counter.", "Besucherzähler öffnen."),
    },
  },
  {
    key: "counter.edit",
    text: {
      section: t("Számláló", "Counter", "Zähler"),
      label: t("Számláló szerkesztés", "Counter edit", "Zähler bearbeiten"),
      description: t("Látogatószámok módosítása.", "Modify visitor counts.", "Besucherzahlen ändern."),
    },
  },
  {
    key: "dashboard.view",
    text: {
      section: t("Irányítópult", "Dashboard", "Dashboard"),
      label: t("Irányítópult megtekintés", "Dashboard view", "Dashboard anzeigen"),
      description: t("Elemzési irányítópult megnyitása.", "Open analytics dashboard.", "Analyse-Dashboard öffnen."),
    },
  },
  {
    key: "dashboard.export",
    text: {
      section: t("Irányítópult", "Dashboard", "Dashboard"),
      label: t("Irányítópult export", "Dashboard export", "Dashboard exportieren"),
      description: t("Dashboard riportok exportálása.", "Export dashboard reports.", "Dashboard-Berichte exportieren."),
    },
  },
  {
    key: "my_schedule.view",
    text: {
      section: t("Saját beosztás", "My Schedule", "Mein Dienstplan"),
      label: t("Saját beosztás felület", "My schedule shell", "Dienstplan-Bereich"),
      description: t("Saját beosztás modul megnyitása.", "Open personal schedule section.", "Persönlichen Dienstplanbereich öffnen."),
    },
  },
  {
    key: "my_schedule.calendar.view",
    text: {
      section: t("Saját beosztás", "My Schedule", "Mein Dienstplan"),
      label: t("Naptár megtekintés", "Calendar view", "Kalender anzeigen"),
      description: t("Kiosztott műszakok megtekintése.", "View assigned shifts.", "Zugewiesene Schichten anzeigen."),
    },
  },
  {
    key: "my_schedule.attendance.mark",
    text: {
      section: t("Saját beosztás", "My Schedule", "Mein Dienstplan"),
      label: t("Jelenlét jelölés", "Mark attendance", "Anwesenheit markieren"),
      description: t("Saját műszak jelenlét rögzítése.", "Submit attendance on shifts.", "Anwesenheit für eigene Schichten erfassen."),
    },
  },
  {
    key: "my_schedule.timeoff.request",
    text: {
      section: t("Saját beosztás", "My Schedule", "Mein Dienstplan"),
      label: t("Szabadságkérelmek", "Time-off requests", "Abwesenheitsanträge"),
      description: t("Szabadságkérelem beküldés és szerkesztés.", "Submit and edit time-off requests.", "Abwesenheitsanträge senden und bearbeiten."),
    },
  },
  {
    key: "my_schedule.availability.submit",
    text: {
      section: t("Saját beosztás", "My Schedule", "Mein Dienstplan"),
      label: t("Elérhetőség megadás", "Availability submit", "Verfügbarkeit senden"),
      description: t("Elérhetőségi preferenciák mentése.", "Submit availability preferences.", "Verfügbarkeitspräferenzen senden."),
    },
  },
  {
    key: "my_schedule.notifications.view",
    text: {
      section: t("Saját beosztás", "My Schedule", "Mein Dienstplan"),
      label: t("Értesítések megtekintés", "Notifications view", "Benachrichtigungen anzeigen"),
      description: t("Beosztási értesítések olvasása.", "Read schedule notifications.", "Dienstplan-Benachrichtigungen lesen."),
    },
  },
  {
    key: "my_schedule.analytics.view",
    text: {
      section: t("Saját beosztás", "My Schedule", "Mein Dienstplan"),
      label: t("Analitika megtekintés", "Analytics view", "Analytik anzeigen"),
      description: t("Személyes havi analitika megjelenítése.", "View personal monthly analytics.", "Persönliche Monatsanalytik anzeigen."),
    },
  },
  {
    key: "my_schedule.calendar.export",
    text: {
      section: t("Saját beosztás", "My Schedule", "Mein Dienstplan"),
      label: t("Naptár export", "Calendar export", "Kalenderexport"),
      description: t("Saját beosztás ICS export.", "Download ICS schedule export.", "Dienstplan als ICS exportieren."),
    },
  },
  {
    key: "schedule_builder.view",
    text: {
      section: t("Beosztástervező", "Schedule Builder", "Dienstplaner"),
      label: t("Tervező hozzáférés", "Builder access", "Planerzugriff"),
      description: t("Beosztás tervező megnyitása.", "Open schedule builder.", "Dienstplaner öffnen."),
    },
  },
  {
    key: "schedule.calendar.manage",
    text: {
      section: t("Beosztástervező", "Schedule Builder", "Dienstplaner"),
      label: t("Naptár kezelés", "Calendar manage", "Kalender verwalten"),
      description: t("Műszakok és naptár kezelése.", "Manage shifts and calendar.", "Schichten und Kalender verwalten."),
    },
  },
  {
    key: "schedule.requests.review",
    text: {
      section: t("Beosztástervező", "Schedule Builder", "Dienstplaner"),
      label: t("Kérelmek elbírálása", "Requests review", "Anträge prüfen"),
      description: t("Munkatársi kérelmek kezelése.", "Review and resolve staff requests.", "Mitarbeiteranträge prüfen und abschließen."),
    },
  },
  {
    key: "schedule.availability.review",
    text: {
      section: t("Beosztástervező", "Schedule Builder", "Dienstplaner"),
      label: t("Elérhetőség felülvizsgálat", "Availability review", "Verfügbarkeiten prüfen"),
      description: t("Nem elérhető jelölések megerősítése vagy lezárása.", "Confirm or remove unavailable-day submissions.", "Nicht-verfügbar-Tage bestätigen oder entfernen."),
    },
  },
  {
    key: "schedule.export.manage",
    text: {
      section: t("Beosztástervező", "Schedule Builder", "Dienstplaner"),
      label: t("Export kezelés", "Export manage", "Exporte verwalten"),
      description: t("Munkaerő exportok indítása és kezelése.", "Run and manage workforce exports.", "Arbeitskräfte-Exporte ausführen und verwalten."),
    },
  },
  {
    key: "schedule.templates.manage",
    text: {
      section: t("Beosztástervező", "Schedule Builder", "Dienstplaner"),
      label: t("Sablon kezelés", "Templates manage", "Vorlagen verwalten"),
      description: t("Sablonok létrehozása és alkalmazása.", "Create and apply templates.", "Vorlagen erstellen und anwenden."),
    },
  },
  {
    key: "schedule.employees.manage",
    text: {
      section: t("Beosztástervező", "Schedule Builder", "Dienstplaner"),
      label: t("Munkatárs profil kezelés", "Employees manage", "Mitarbeiterprofile verwalten"),
      description: t("Tervezési munkatárs profilok szerkesztése.", "Edit planning employee profiles.", "Planungs-Mitarbeiterprofile bearbeiten."),
    },
  },
  {
    key: "schedule.settings.manage",
    text: {
      section: t("Beosztástervező", "Schedule Builder", "Dienstplaner"),
      label: t("Beállítások kezelés", "Settings manage", "Einstellungen verwalten"),
      description: t("Munkapontok, szerepkörök és szabályok kezelése.", "Manage work points, roles, and rules.", "Arbeitsorte, Rollen und Regeln verwalten."),
    },
  },
  {
    key: "schedule.attendance.manage",
    text: {
      section: t("Beosztástervező", "Schedule Builder", "Dienstplaner"),
      label: t("Jelenlét kezelés", "Attendance manage", "Anwesenheit verwalten"),
      description: t("Jelenléti napló és korrekciók kezelése.", "Manage attendance and correction queue.", "Anwesenheit und Korrekturwarteschlange verwalten."),
    },
  },
  {
    key: "schedule.audit.view",
    text: {
      section: t("Beosztástervező", "Schedule Builder", "Dienstplaner"),
      label: t("Audit napló megtekintés", "Audit view", "Auditprotokoll anzeigen"),
      description: t("Beosztási audit napló megtekintése.", "View schedule audit logs.", "Dienstplan-Auditprotokolle ansehen."),
    },
  },
  {
    key: "ai_center.view",
    text: {
      section: t("AI Központ", "AI Center", "AI-Zentrum"),
      label: t("AI Központ megtekintés", "AI center view", "AI-Zentrum anzeigen"),
      description: t("AI Központ felület megnyitása.", "Open AI center.", "AI-Zentrum öffnen."),
    },
  },
  {
    key: "ai.knowledge.manage",
    text: {
      section: t("AI Központ", "AI Center", "AI-Zentrum"),
      label: t("Tudásbázis kezelés", "Knowledge manage", "Wissensbasis verwalten"),
      description: t("AI tudásforrások kezelése.", "Manage AI knowledge sources.", "AI-Wissensquellen verwalten."),
    },
  },
  {
    key: "ai.updates.manage",
    text: {
      section: t("AI Központ", "AI Center", "AI-Zentrum"),
      label: t("Frissítések kezelés", "Updates manage", "Updates verwalten"),
      description: t("AI frissítések kezelése.", "Manage AI updates.", "AI-Updates verwalten."),
    },
  },
  {
    key: "ai.analytics.view",
    text: {
      section: t("AI Központ", "AI Center", "AI-Zentrum"),
      label: t("AI analitika megtekintés", "AI analytics view", "AI-Analytik anzeigen"),
      description: t("AI analitikai felületek megnyitása.", "View AI analytics surfaces.", "AI-Analyseoberflächen anzeigen."),
    },
  },
  {
    key: "admin.staff.view",
    text: {
      section: t("Admin munkatárs", "Admin Staff", "Admin Personal"),
      label: t("Munkatárs kezelés megtekintés", "Manage staff view", "Personalverwaltung anzeigen"),
      description: t("Munkatárs kezelő megnyitása.", "Open manage staff tool.", "Personalverwaltungsseite öffnen."),
    },
  },
  {
    key: "admin.staff.account.create",
    text: {
      section: t("Admin munkatárs", "Admin Staff", "Admin Personal"),
      label: t("Fiók létrehozás", "Create accounts", "Konten erstellen"),
      description: t("Új felhasználói fiókok létrehozása.", "Create user accounts.", "Benutzerkonten erstellen."),
    },
  },
  {
    key: "admin.staff.account.delete",
    text: {
      section: t("Admin munkatárs", "Admin Staff", "Admin Personal"),
      label: t("Fiók törlés", "Delete accounts", "Konten löschen"),
      description: t("Felhasználói fiókok törlése.", "Delete user accounts.", "Benutzerkonten löschen."),
    },
  },
  {
    key: "admin.staff.account.reset_password",
    text: {
      section: t("Admin munkatárs", "Admin Staff", "Admin Personal"),
      label: t("Jelszó visszaállítás", "Reset passwords", "Passwörter zurücksetzen"),
      description: t("Jelszó-visszaállító e-mail küldése.", "Trigger password reset emails.", "Passwort-Reset-E-Mails auslösen."),
    },
  },
  {
    key: "admin.staff.permissions.manage",
    text: {
      section: t("Admin munkatárs", "Admin Staff", "Admin Personal"),
      label: t("Jogosultság kezelés", "Manage permissions", "Berechtigungen verwalten"),
      description: t("Felhasználói jogosultságok kezelése.", "Grant and revoke user permissions.", "Benutzerberechtigungen erteilen und entziehen."),
    },
  },
  {
    key: "admin.staff.role.assign_admin",
    text: {
      section: t("Admin munkatárs", "Admin Staff", "Admin Personal"),
      label: t("Admin szerepkör kiosztás", "Assign admin role", "Adminrolle zuweisen"),
      description: t("Felhasználó adminná emelése.", "Grant admin role to a user.", "Einem Benutzer die Adminrolle zuweisen."),
    },
  },
];
